// First-run age screen state (DESIGN 5.1; PRD 13; LAUNCH_CHECKLIST B1, M11). The server stores only the age bracket and
// refuses a different answer from the same device within 24 hours (profile.age_locked); this device keeps its own answer
// for those 24 hours too, so a lost session is recreated with the same answer instead of asking again. After 24 hours the
// birth month and year are deleted from the device: the session purges them on every boot, whenever the page wakes up,
// and with a timer at the deadline (session.ts).

import type { PlatformStorage } from '../platform/storage';
import { readJson, writeJson } from '../platform/storage';

export interface AgeAnswer {
  /** 1..12 */
  birthMonth: number;
  birthYear: number;
}

const ANSWER_KEY = 'cw_age_answer';
const DONE_KEY = 'cw_age_done';
const PHOTO_KEY = 'cw_photosensitivity_seen';
export const AGE_LOCK_MS = 24 * 60 * 60 * 1000;
/** Oldest year of birth the screen offers. */
export const AGE_YEARS_BACK = 100;

/** The years the screen offers, newest first; no default is preselected (DESIGN 6.2). */
export function birthYears(now: Date): number[] {
  const y = now.getUTCFullYear();
  return Array.from({ length: AGE_YEARS_BACK + 1 }, (_, i) => y - i);
}

/** The answer, or null when it is incomplete or lies in the future. */
export function validateAge(month: number | null, year: number | null, now: Date): AgeAnswer | null {
  if (month === null || year === null) return null;
  if (!Number.isInteger(month) || month < 1 || month > 12) return null;
  const nowYear = now.getUTCFullYear();
  if (!Number.isInteger(year) || year > nowYear || year < nowYear - AGE_YEARS_BACK) return null;
  if (year === nowYear && month > now.getUTCMonth() + 1) return null;
  return { birthMonth: month, birthYear: year };
}

function isStoredAnswer(v: unknown): v is AgeAnswer & { at: number } {
  if (typeof v !== 'object' || v === null) return false;
  const a = v as Record<string, unknown>;
  return typeof a.birthMonth === 'number' && typeof a.birthYear === 'number' && typeof a.at === 'number';
}

export function rememberAgeAnswer(storage: PlatformStorage, a: AgeAnswer, nowMs: number): void {
  writeJson(storage, ANSWER_KEY, { ...a, at: nowMs });
  markAgeScreenDone(storage);
}

/** The stored answer while its 24 hours last; an expired, future-dated or unreadable one is deleted. */
function keptAnswer(storage: PlatformStorage, nowMs: number): (AgeAnswer & { at: number }) | null {
  if (storage.get(ANSWER_KEY) === null) return null;
  const stored = readJson(storage, ANSWER_KEY);
  if (!isStoredAnswer(stored) || nowMs - stored.at >= AGE_LOCK_MS || stored.at > nowMs) {
    storage.remove(ANSWER_KEY);
    return null;
  }
  return stored;
}

/** The answer given on this device within the last 24 hours; an older one is deleted. */
export function lockedAgeAnswer(storage: PlatformStorage, nowMs: number): AgeAnswer | null {
  const stored = keptAnswer(storage, nowMs);
  return stored === null ? null : { birthMonth: stored.birthMonth, birthYear: stored.birthYear };
}

/** Deletes an answer whose 24 hours are over; returns when the answer still kept expires (unix ms), or null. */
export function purgeAgeAnswer(storage: PlatformStorage, nowMs: number): number | null {
  const stored = keptAnswer(storage, nowMs);
  return stored === null ? null : stored.at + AGE_LOCK_MS;
}

/** True once the age screen was completed on this device (no birth data; lets offline practice start). */
export function ageScreenDone(storage: PlatformStorage): boolean {
  return storage.get(DONE_KEY) === '1';
}

/** Also set once the server knows the account: its age band answered the age screen (DESIGN 5.1). */
export function markAgeScreenDone(storage: PlatformStorage): void {
  storage.set(DONE_KEY, '1');
}

export function photosensitivitySeen(storage: PlatformStorage): boolean {
  return storage.get(PHOTO_KEY) === '1';
}

export function markPhotosensitivitySeen(storage: PlatformStorage): void {
  storage.set(PHOTO_KEY, '1');
}
